/**
 * LAGOSEvidenceVault — /state/lagos/evidence — Enterprise Phase 2
 * ================================================================
 * Enterprise MRV Evidence Management with:
 *   - Emergent Object Storage (enterprise-grade, production-ready)
 *   - Dynamic governance rules (admin-configurable)
 *   - Automated document expiry
 *   - Methodology dependency blocking
 *   - Bulk review operations
 *   - Mobile/field geo-tagging
 *   - Full audit trail + version snapshots
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import { toast } from "sonner";
import {
  Shield, Target, CheckCircle2, AlertTriangle, XCircle,
  Upload, Download, Eye, FileText, Clock, Lock,
  Search, RefreshCw, Activity, Zap, MapPin, Camera,
  CheckSquare, Square, ChevronRight,
} from "lucide-react";

// ── API Base ─────────────────────────────────────────────────────────────────
const apiBase = () => process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` });
const apiFetch = (path, opts = {}) =>
  fetch(`${apiBase()}/api${path}`, { ...opts, headers: { ...authHeader(), ...(opts.headers || {}) } });

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtPct  = v => `${Number(v || 0).toFixed(1)}%`;
const fmtSize = v => { if (!v) return "—"; if (v >= 1e6) return `${(v/1e6).toFixed(1)}MB`; if (v >= 1000) return `${(v/1024).toFixed(1)}KB`; return `${v}B`; };

// ── Design constants ──────────────────────────────────────────────────────────
const TIER_STYLE = {
  High:        { bg:"#ecfdf5", border:"#6ee7b7", text:"#065f46", dot:"#059669" },
  Medium:      { bg:"#fffbeb", border:"#fcd34d", text:"#92400e", dot:"#f59e0b" },
  Low:         { bg:"#fef2f2", border:"#fca5a5", text:"#b91c1c", dot:"#ef4444" },
  Insufficient:{ bg:"#fef2f2", border:"#fca5a5", text:"#b91c1c", dot:"#ef4444" },
};

const STATUS_STYLE = {
  submitted:             { bg:"#eff6ff", text:"#1d4ed8", label:"Submitted"  },
  under_review:          { bg:"#fef9c3", text:"#854d0e", label:"Under Review"},
  approved:              { bg:"#ecfdf5", text:"#065f46", label:"Approved"   },
  rejected:              { bg:"#fef2f2", text:"#991b1b", label:"Rejected"   },
  expired:               { bg:"#f8fafc", text:"#64748b", label:"Expired"    },
  archived:              { bg:"#f1f5f9", text:"#94a3b8", label:"Archived"   },
  request_clarification: { bg:"#fef9c3", text:"#854d0e", label:"Clarification"},
};

const DOC_TYPES = [
  { value:"kyc",               label:"KYC / Registration",        checklist:"kyc_registration"    },
  { value:"utility_bill",      label:"Utility Bill / Fuel Record", checklist:"utility_bills"       },
  { value:"production_record", label:"Production / Output Record", checklist:"production_records"  },
  { value:"waste_record",      label:"Waste Diversion Record",     checklist:"waste_records"       },
  { value:"financial_record",  label:"Financial Record / Invoice", checklist:"financial_records"   },
  { value:"site_photo",        label:"Site Photo / Geo-tagged",    checklist:"site_photos"         },
  { value:"weighbridge",       label:"Weighbridge Ticket",         checklist:"weighbridge_tickets" },
  { value:"throughput_log",    label:"Throughput / Bale Log",      checklist:"throughput_logs"     },
  { value:"meter_gen_log",     label:"Meter Generation Log",       checklist:"meter_gen_logs"      },
  { value:"commissioning",     label:"Commissioning Certificate",  checklist:"commissioning_certs" },
  { value:"feedstock_log",     label:"Feedstock / Input Log",      checklist:"feedstock_logs"      },
  { value:"methane_monitoring","label":"Methane Monitoring Data",  checklist:"methane_monitoring"  },
  { value:"offtake_agreement", label:"Offtake Agreement",          checklist:"offtake_agreements"  },
  { value:"fleet_log",         label:"Fleet / GPS Log",            checklist:"fleet_logs"          },
  { value:"other",             label:"Other",                      checklist:""                    },
];

// ── Sub-components ────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.submitted;
  return (
    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const t = TIER_STYLE[tier] || TIER_STYLE.Insufficient;
  return (
    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border" style={{ background: t.bg, borderColor: t.border, color: t.text }}>
      {tier}
    </span>
  );
};

const ConfBar = ({ score, tier }) => {
  const t = TIER_STYLE[tier] || TIER_STYLE.Insufficient;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: score + "%", background: t.dot }} />
      </div>
      <span className="text-[10px] font-black w-8 text-right" style={{ color: t.dot }}>{score.toFixed(0)}</span>
    </div>
  );
};

// ── Upload Panel — with geo-tagging ──────────────────────────────────────────
function UploadPanel({ entityId, periodId, onUploaded }) {
  const [files, setFiles]       = useState([]);
  const [docType, setDocType]   = useState("kyc");
  const [notes, setNotes]       = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle|requesting|captured|denied
  const [geoCoords, setGeoCoords] = useState(null);
  const inputRef = useRef();

  const selectedType = DOC_TYPES.find(d => d.value === docType) || DOC_TYPES[0];

  const handleFiles = (fileList) => setFiles(Array.from(fileList).slice(0, 5));

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const captureGeo = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGeoStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setGeoCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, ts: new Date().toISOString() });
        setGeoStatus("captured");
        toast.success("Location captured");
      },
      () => { setGeoStatus("denied"); toast.error("Location access denied"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleUpload = async () => {
    if (!files.length) { toast.error("Select at least one file"); return; }
    setUploading(true);
    let successCount = 0;
    const deviceMeta = navigator.userAgent?.slice(0, 100) || "";
    for (const file of files) {
      const fd = new FormData();
      fd.append("file",             file);
      fd.append("doc_type",         docType);
      fd.append("sub_type",         selectedType.label);
      fd.append("checklist_key",    selectedType.checklist);
      fd.append("is_critical",      "false");
      fd.append("reporting_period", periodId);
      fd.append("notes",            notes);
      fd.append("device_meta",      deviceMeta);
      if (geoCoords) {
        fd.append("geo_lat",      String(geoCoords.lat));
        fd.append("geo_lng",      String(geoCoords.lng));
        fd.append("geo_accuracy", String(geoCoords.acc));
        fd.append("geo_timestamp",geoCoords.ts);
      }
      try {
        const r = await apiFetch(`/lagos/vault/upload/${entityId}/${periodId}`, { method: "POST", body: fd });
        if (!r.ok) throw new Error(await r.text());
        successCount++;
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? "s" : ""} uploaded${geoCoords ? " (geo-tagged)" : ""}`);
      setFiles([]); setNotes(""); setGeoCoords(null); setGeoStatus("idle");
      onUploaded();
    }
  };

  return (
    <div className="space-y-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-700">Upload Documents</p>
        {/* Storage indicator */}
        <span className="text-[9px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded font-bold">
          Enterprise Storage Active
        </span>
      </div>

      {/* Drag/drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
          dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-400 hover:bg-slate-100"
        }`}
        data-testid="drop-zone"
      >
        <Upload size={18} className="mx-auto mb-1.5 text-slate-400" />
        <p className="text-xs font-medium text-slate-600">
          {files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Drag & drop or click to select"}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">PDF · XLS/XLSX · CSV · JPG/PNG · DOC/DOCX · Max 50MB</p>
        <input ref={inputRef} type="file" multiple className="hidden"
          accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.doc,.docx"
          onChange={e => handleFiles(e.target.files)} data-testid="file-input" />
      </div>

      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-white border border-slate-100 rounded-lg px-3 py-1.5 text-xs">
              <span className="text-slate-600 truncate max-w-[200px]">{f.name}</span>
              <span className="text-slate-400 flex-shrink-0">{fmtSize(f.size)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Document type + geo row */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] text-slate-500 mb-1 font-medium">Document Type</p>
          <select value={docType} onChange={e => setDocType(e.target.value)}
            className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
            data-testid="doc-type-select">
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 mb-1 font-medium">Notes</p>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes…"
            className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
            data-testid="upload-notes" />
        </div>
      </div>

      {/* Geo-tagging — field operations */}
      <div className="flex items-center gap-3">
        <button onClick={captureGeo} disabled={geoStatus === "captured"}
          className={`flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-lg border font-medium transition-all ${
            geoStatus === "captured" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : geoStatus === "requesting" ? "bg-slate-50 text-slate-400 border-slate-200"
            : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700"
          }`}
          data-testid="geo-capture-btn">
          <MapPin size={11} />
          {geoStatus === "captured" ? `Geo: ${geoCoords?.lat?.toFixed(4)}, ${geoCoords?.lng?.toFixed(4)}`
           : geoStatus === "requesting" ? "Getting location…"
           : "Capture Location"}
        </button>
        <span className="text-[9px] text-slate-400">Optional: geo-tags document for field verification</span>
      </div>

      <button onClick={handleUpload} disabled={uploading || !files.length}
        className="w-full flex items-center justify-center gap-2 bg-[#0f172a] text-white text-xs py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-50 font-bold"
        data-testid="upload-submit-btn">
        {uploading
          ? <><RefreshCw size={12} className="animate-spin" /> Uploading to Enterprise Storage…</>
          : <><Upload size={12} /> Upload {files.length > 0 ? `${files.length} ` : ""}Document{files.length !== 1 ? "s" : ""}{geoCoords ? " (Geo-tagged)" : ""}</>
        }
      </button>
    </div>
  );
}

// ── Document Row — with geo badge, expiry, storage info ──────────────────────
function DocumentRow({ doc, onAction, onDownload }) {
  const [showActions, setShowActions] = useState(false);
  const [notes, setNotes] = useState("");

  const handleAction = async (action) => {
    await onAction(doc.doc_id, action, notes);
    setShowActions(false); setNotes("");
  };

  const isGeoTagged = !!(doc.geo_lat && doc.geo_lng);
  const now = new Date().toISOString().slice(0, 10);
  const isExpired   = doc.calculated_expiry && doc.calculated_expiry < now;
  const expiringSoon = doc.calculated_expiry && !isExpired &&
    doc.calculated_expiry <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`doc-row-${doc.doc_id}`}>
      <div className="flex items-start gap-3 p-3 bg-white hover:bg-slate-50">
        <FileText size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-slate-700 truncate">{doc.file_name}</p>
            <StatusBadge status={doc.status} />
            {doc.is_critical && <span className="text-[8px] bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 rounded font-bold">CRITICAL</span>}
            {isGeoTagged && (
              <span className="text-[8px] bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                <MapPin size={8} /> Geo-Verified
              </span>
            )}
            {isExpired && <span className="text-[8px] bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 rounded font-bold">EXPIRED</span>}
            {expiringSoon && !isExpired && <span className="text-[8px] bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded font-bold">EXPIRING SOON</span>}
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-[9px] text-slate-400 capitalize">{(doc.doc_type || "").replace(/_/g, " ")}</span>
            <span className="text-[9px] text-slate-400">{fmtSize(doc.file_size)}</span>
            <span className="text-[9px] text-slate-400">{doc.upload_timestamp?.slice(0, 10) || "—"}</span>
            {doc.storage_provider && <span className="text-[8px] text-slate-300 capitalize">{doc.storage_provider}</span>}
            {doc.calculated_expiry && (
              <span className={`text-[9px] ${isExpired ? "text-red-500" : expiringSoon ? "text-amber-600" : "text-slate-400"}`}>
                exp: {doc.calculated_expiry}
              </span>
            )}
          </div>
          {doc.reviewer_notes && <p className="text-[9px] text-slate-500 mt-1 italic">{doc.reviewer_notes}</p>}
          {isGeoTagged && (
            <p className="text-[9px] text-emerald-600 mt-0.5">
              {doc.geo_lat?.toFixed(4)}°N, {doc.geo_lng?.toFixed(4)}°E {doc.geo_accuracy ? `(±${doc.geo_accuracy?.toFixed(0)}m)` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onDownload(doc.doc_id, doc.file_name)}
            className="p-1.5 text-slate-400 hover:text-blue-600 rounded" title="Download">
            <Download size={12} />
          </button>
          <button onClick={() => setShowActions(!showActions)}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded" title="Review actions">
            <Eye size={12} />
          </button>
        </div>
      </div>

      {showActions && (
        <div className="border-t border-slate-100 p-3 bg-slate-50 space-y-2">
          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Review notes (required for reject / clarification)…"
            className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white" />
          <div className="flex flex-wrap gap-2">
            {[
              { action:"approve",               label:"Approve",       color:"bg-emerald-600 hover:bg-emerald-700" },
              { action:"reject",                label:"Reject",        color:"bg-red-600 hover:bg-red-700"         },
              { action:"request_clarification", label:"Clarification", color:"bg-amber-500 hover:bg-amber-600"   },
              { action:"flag_high_risk",        label:"Flag Risk",     color:"bg-orange-500 hover:bg-orange-600"   },
              { action:"mark_expired",          label:"Expired",       color:"bg-slate-500 hover:bg-slate-600"    },
            ].map(({ action, label, color }) => (
              <button key={action} onClick={() => handleAction(action)}
                className={`${color} text-white text-[10px] px-3 py-1.5 rounded-lg font-bold`}
                data-testid={`action-${action}-${doc.doc_id}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Checklist Panel ───────────────────────────────────────────────────────────
function ChecklistPanel({ checklist }) {
  const universal = checklist.filter(c => c.category === "Universal");
  const sectorSpecific = checklist.filter(c => c.category !== "Universal");

  const renderItem = (item) => {
    const statusIcon = item.status === "approved"  ? <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                    : item.status === "uploaded"    ? <Clock size={12} className="text-amber-500 flex-shrink-0" />
                    : item.status === "rejected"    ? <XCircle size={12} className="text-red-500 flex-shrink-0" />
                    : item.status === "not_applicable" ? <span className="text-[9px] text-slate-400 flex-shrink-0">N/A</span>
                    : <div className="w-3 h-3 rounded-full border-2 border-slate-200 flex-shrink-0" />;
    return (
      <div key={item.key} className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
        {statusIcon}
        <div className="flex-1 min-w-0">
          <p className={`text-xs ${item.is_critical ? "font-semibold text-slate-700" : "text-slate-600"}`}>{item.label}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.is_critical && <span className="text-[8px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-bold border border-red-100">Critical</span>}
          <span className={`text-[9px] font-medium capitalize ${
            item.status === "approved" ? "text-emerald-600"
            : item.status === "uploaded" ? "text-amber-600"
            : item.status === "rejected" ? "text-red-500"
            : "text-slate-400"
          }`}>{item.status.replace(/_/g, " ")}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {universal.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Universal Requirements</p>
          {universal.map(renderItem)}
        </div>
      )}
      {sectorSpecific.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Sector-Specific ({sectorSpecific[0]?.category})</p>
          {sectorSpecific.map(renderItem)}
        </div>
      )}
    </div>
  );
}

// ── Entity Detail Drawer ──────────────────────────────────────────────────────
function EntityDetailDrawer({ entityId, periodId, onClose }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/lagos/vault/entity/${entityId}/${periodId}`);
      if (!r.ok) throw new Error(r.statusText);
      setDetail(await r.json());
    } catch (e) { toast.error("Failed to load entity data"); }
    finally { setLoading(false); }
  }, [entityId, periodId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (docId, action, notes) => {
    try {
      const r = await apiFetch(`/lagos/vault/document/${docId}/review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, notes }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(`Document ${action.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error("Action failed: " + e.message); }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const r = await apiFetch(`/lagos/vault/document/${docId}/download`);
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
      load();
    } catch { toast.error("Download failed"); }
  };

  if (loading || !detail) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white shadow-xl flex items-center justify-center" onClick={e => e.stopPropagation()}>
        <div className="text-center"><RefreshCw size={20} className="animate-spin text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-400">Loading…</p></div>
      </div>
    </div>
  );

  const { entity, confidence: conf, documents, checklist, audit } = detail;
  const tierStyle = TIER_STYLE[conf?.tier] || TIER_STYLE.Insufficient;

  const TABS = [
    { id:"documents",   label:"Documents",   count: documents?.length },
    { id:"checklist",   label:"Checklist",   count: checklist?.filter(c => c.status === "approved").length + "/" + checklist?.length },
    { id:"review",      label:"Review Queue", count: documents?.filter(d => d.status === "under_review" || d.status === "submitted").length },
    { id:"audit",       label:"Audit Trail",  count: audit?.length },
    { id:"map",         label:"Geo Map",      count: documents?.filter(d => d.geo_lat).length || null },
    { id:"status",      label:"Readiness",   count: null },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white shadow-xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#0f172a] text-white p-5 flex-shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0">
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{entity?.sector?.replace(/_/g, " ")} · {entity?.lga}</p>
              <p className="text-base font-black truncate">{entity?.name}</p>
              <p className="text-[10px] opacity-40 mt-0.5">{entityId}</p>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white ml-4">✕</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[9px] opacity-40">Evidence Score</p>
              <p className="text-lg font-black" style={{ color: tierStyle.dot }}>{conf?.score?.toFixed(0)}/100</p>
            </div>
            <div>
              <p className="text-[9px] opacity-40">Confidence Tier</p>
              <TierBadge tier={conf?.tier} />
            </div>
            <div>
              <p className="text-[9px] opacity-40">Total Docs</p>
              <p className="text-lg font-black text-white">{documents?.length || 0}</p>
            </div>
          </div>
          {/* Readiness flags */}
          <div className="flex flex-wrap gap-2 mt-3">
            {conf?.vcm_ready  && <span className="text-[9px] bg-emerald-900/60 border border-emerald-700 px-2 py-0.5 rounded font-bold">VCM Ready</span>}
            {conf?.art6_ready && <span className="text-[9px] bg-blue-900/60 border border-blue-700 px-2 py-0.5 rounded font-bold">Art.6 Pre-Qualified</span>}
            {conf?.bond_ready && <span className="text-[9px] bg-purple-900/60 border border-purple-700 px-2 py-0.5 rounded font-bold">Bond Ready</span>}
            {conf?.needs_ta   && <span className="text-[9px] bg-amber-900/60 border border-amber-700 px-2 py-0.5 rounded font-bold">Needs TA</span>}
            {conf?.blocked    && <span className="text-[9px] bg-red-900/60 border border-red-700 px-2 py-0.5 rounded font-bold">Blocked</span>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 flex-shrink-0 bg-slate-50 px-2 pt-2 gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-3 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition-all ${
                activeTab === t.id ? "bg-white border border-b-white border-slate-200 text-slate-800 -mb-px" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${t.id}`}>
              {t.label}
              {t.count !== null && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">{t.count}</span>}
            </button>
          ))}
        </div>

        {/* Tab content — scrollable */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* ── DOCUMENTS tab ──────────────────────────────────────────────── */}
          {activeTab === "documents" && (
            <div className="space-y-4">
              <UploadPanel entityId={entityId} periodId={periodId} onUploaded={load} />
              {documents?.length === 0
                ? <p className="text-sm text-slate-400 text-center py-6">No documents uploaded yet.</p>
                : <div className="space-y-2">
                    {documents.filter(d => d.status !== "archived").map(doc => (
                      <DocumentRow key={doc.doc_id} doc={doc} onAction={handleAction} onDownload={handleDownload} />
                    ))}
                    {documents.some(d => d.status === "archived") && (
                      <p className="text-[10px] text-slate-400 text-center pt-2">
                        + {documents.filter(d => d.status === "archived").length} archived documents
                      </p>
                    )}
                  </div>
              }
            </div>
          )}

          {/* ── CHECKLIST tab ───────────────────────────────────────────────── */}
          {activeTab === "checklist" && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  ["Total Items",      checklist?.length,                                                       "#0f172a"],
                  ["Complete / Approved", checklist?.filter(c => c.status === "approved").length,              "#059669"],
                  ["Critical Items",   checklist?.filter(c => c.is_critical).length + " required",             "#dc2626"],
                ].map(([k, v, c]) => (
                  <div key={k} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-[9px] text-slate-400">{k}</p>
                    <p className="text-lg font-black mt-0.5" style={{ color: c }}>{v}</p>
                  </div>
                ))}
              </div>
              <ChecklistPanel checklist={checklist || []} />
            </div>
          )}

          {/* ── REVIEW tab ──────────────────────────────────────────────────── */}
          {activeTab === "review" && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-medium">Documents requiring review action:</p>
              {documents?.filter(d => ["submitted","under_review"].includes(d.status)).length === 0
                ? <div className="text-center py-8 text-slate-400"><CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-400" /><p className="text-sm">No documents pending review</p></div>
                : documents?.filter(d => ["submitted","under_review"].includes(d.status)).map(doc => (
                    <DocumentRow key={doc.doc_id} doc={doc} onAction={handleAction} onDownload={handleDownload} />
                  ))
              }
            </div>
          )}

          {/* ── AUDIT TRAIL tab ─────────────────────────────────────────────── */}
          {activeTab === "audit" && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Lock size={12} className="text-slate-400" />
                <p className="text-[10px] text-slate-400 font-medium">Immutable audit trail — every action logged</p>
              </div>
              {audit?.length === 0
                ? <p className="text-sm text-slate-400 text-center py-6">No audit entries yet</p>
                : <div className="space-y-1">
                    {audit?.map(entry => (
                      <div key={entry.id} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              entry.action_type?.includes("UPLOAD") ? "bg-blue-50 text-blue-700"
                              : entry.action_type?.includes("APPROVE") ? "bg-emerald-50 text-emerald-700"
                              : entry.action_type?.includes("REJECT") ? "bg-red-50 text-red-700"
                              : "bg-slate-100 text-slate-600"
                            }`}>{entry.action_type}</span>
                            <span className="text-[9px] text-slate-400">{entry.timestamp?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          {entry.notes && <p className="text-[10px] text-slate-500 mt-0.5">{entry.notes}</p>}
                          {entry.user_email && <p className="text-[9px] text-slate-400">{entry.user_email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </div>
          )}

          {/* ── GEO MAP tab ─────────────────────────────────────────────── */}
          {activeTab === "map" && (
            <GeoMapTab entity={entity} documents={documents || []} />
          )}

          {/* ── VERIFICATION STATUS tab ───────────────────────────────────── */}
          {activeTab === "status" && (
            <div className="space-y-4">
              {/* Score breakdown */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-slate-700">Confidence Score Breakdown</p>
                {[
                  { label:"Approval Rate",     val: conf?.approval_rate,  note:"35% weight — approved docs / active docs" },
                  { label:"Checklist Rate",     val: conf?.checklist_rate, note:"40% weight — items completed / total items" },
                  { label:"Critical Coverage",  val: conf?.critical_rate,  note:"25% weight — critical items complete / total critical" },
                ].map(({ label, val, note }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-600">{label}</span>
                      <span className="font-black text-slate-800">{fmtPct(val)}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: (val || 0) + "%" }} />
                    </div>
                    <p className="text-[9px] text-slate-400 mt-0.5">{note}</p>
                  </div>
                ))}
              </div>

              {/* Methodology dependency blocking */}
              {conf?.methodology_blocked && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-red-800 mb-2">
                    Methodology Blocked — {conf.dep_pathway}
                  </p>
                  <p className="text-[10px] text-red-700 mb-2">
                    The following required evidence items are missing. Pathway eligibility is blocked until resolved.
                  </p>
                  {(conf.blocking_deps || []).map(dep => (
                    <div key={dep.key} className="flex items-center gap-2 py-1.5 border-b border-red-100 last:border-0">
                      <XCircle size={11} className="text-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] font-bold text-red-800">{dep.label || dep.key}</p>
                        <p className="text-[9px] text-red-600 capitalize">Status: {dep.status?.replace(/_/g," ")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Readiness flags */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Readiness Assessment</p>
                <div className="space-y-2">
                  {[
                    { key:"vcm_ready",  label:"VCM Ready",           req:"Score ≥65 · ≥3 approved · critical ≥80%"  },
                    { key:"art6_ready", label:"Art.6.4 Pre-Qualified", req:"Score ≥80 · ≥4 approved · critical 100%"  },
                    { key:"bond_ready", label:"Green Bond Ready",     req:"Score ≥70 · ≥3 approved · critical ≥80%"  },
                    { key:"needs_ta",   label:"Needs Technical Asst.", req:"Score <50 or critical docs <50%"          },
                    { key:"blocked",    label:"Blocked",              req:"No active docs or no critical docs"         },
                  ].map(({ key, label, req }) => (
                    <div key={key} className={`flex items-center justify-between rounded-lg px-3 py-2.5 ${
                      conf?.[key] ? (key === "blocked" || key === "needs_ta" ? "bg-red-50 border border-red-100" : "bg-emerald-50 border border-emerald-100")
                                  : "bg-slate-50 border border-slate-100"
                    }`}>
                      <div>
                        <p className={`text-xs font-bold ${
                          conf?.[key] ? (key === "blocked" || key === "needs_ta" ? "text-red-700" : "text-emerald-700") : "text-slate-400"
                        }`}>{label}</p>
                        <p className="text-[9px] text-slate-400">{req}</p>
                      </div>
                      {conf?.[key]
                        ? (key === "blocked" || key === "needs_ta"
                            ? <XCircle size={16} className="text-red-500" />
                            : <CheckCircle2 size={16} className="text-emerald-500" />)
                        : <div className="w-4 h-4 rounded-full border-2 border-slate-200" />
                      }
                    </div>
                  ))}
                </div>
              </div>

              {/* Active flags */}
              {conf?.flags?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-amber-800 mb-2">Active Risk Flags</p>
                  {conf.flags.map(f => (
                    <div key={f} className="flex items-start gap-2 text-xs text-amber-700">
                      <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Geo Map Tab — lazy-loaded to avoid SSR issues ───────────────────────────
const GeoMapViewer = React.lazy(() => import("@/components/GeoMapViewer"));

class GeoMapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prevProps) {
    if (prevProps.entityId !== this.props.entityId && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
          <p className="text-sm text-slate-400">Map temporarily unavailable.</p>
          <p className="text-[10px] text-slate-300 mt-1">Try switching to another tab and back.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function GeoMapTab({ entity, documents }) {
  return (
    <GeoMapErrorBoundary entityId={entity?.entity_id}>
      <React.Suspense fallback={<div className="p-4 text-slate-400 text-sm">Loading map…</div>}>
        <GeoMapViewer entity={entity} geoDocuments={documents} />
      </React.Suspense>
    </GeoMapErrorBoundary>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function LAGOSEvidenceVault() {
  const { selectedPeriod, loading: ctxLoading } = useLagos();
  const [vaultData, setVaultData]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [search, setSearch]         = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [sortBy, setSortBy]         = useState("score_asc");
  // Bulk review state
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [bulkAction, setBulkAction] = useState("approve");
  const [bulkNotes, setBulkNotes]   = useState("");
  const [bulking, setBulking]       = useState(false);
  // Reviewer queue tab
  const [activeView, setActiveView] = useState("entities"); // entities | reviewer_queue | expiry
  // Governance sweep state
  const [lastSweep, setLastSweep]   = useState(null);
  const [sweeping, setSweeping]     = useState(false);

  const loadVault = useCallback(async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    try {
      const [vaultR, govR] = await Promise.all([
        apiFetch(`/lagos/vault/${selectedPeriod}`),
        apiFetch(`/lagos/vault/governance/config`),
      ]);
      if (vaultR.ok) setVaultData(await vaultR.json());
      if (govR.ok) {
        const g = await govR.json();
        setLastSweep(g.last_sweep ? g.last_sweep.slice(0, 16).replace("T", " ") : null);
      }
    } catch { toast.error("Failed to load vault data"); }
    finally { setLoading(false); }
  }, [selectedPeriod]);

  const runSweep = async () => {
    if (!selectedPeriod) return;
    setSweeping(true);
    try {
      const r = await apiFetch(`/lagos/vault/governance/sweep/${selectedPeriod}`, { method: "POST" });
      if (!r.ok) throw new Error();
      const d = await r.json();
      toast.success(`Sweep complete: ${d.expired_count} expired, ${d.methodology_blocked_count} blocked`);
      loadVault();
    } catch { toast.error("Sweep failed"); }
    finally { setSweeping(false); }
  };

  useEffect(() => { loadVault(); }, [loadVault]);

  if (ctxLoading || loading) return <div className="p-8 text-slate-400 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading Evidence Vault…</div>;

  const kpis  = vaultData?.kpis   || {};
  const ents  = vaultData?.entities || [];
  const portScore = vaultData?.portfolio_score || 0;

  // Filter + sort
  let displayed = ents.filter(e => {
    const conf = e.confidence || {};
    if (filterTier !== "all" && conf.tier !== filterTier) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.name?.toLowerCase().includes(q) || e.lga?.toLowerCase().includes(q) || e.sector?.toLowerCase().includes(q);
    }
    return true;
  });
  if (sortBy === "score_asc")  displayed = displayed.sort((a, b) => (a.confidence?.score || 0) - (b.confidence?.score || 0));
  if (sortBy === "score_desc") displayed = displayed.sort((a, b) => (b.confidence?.score || 0) - (a.confidence?.score || 0));
  if (sortBy === "name")       displayed = displayed.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Analytics
  const byLGA = {};
  ents.forEach(e => {
    if (!byLGA[e.lga]) byLGA[e.lga] = { count: 0, totalScore: 0 };
    byLGA[e.lga].count++;
    byLGA[e.lga].totalScore += e.confidence?.score || 0;
  });
  const lgaRanked = Object.entries(byLGA)
    .map(([lga, d]) => ({ lga, avgScore: d.totalScore / d.count, count: d.count }))
    .sort((a, b) => a.avgScore - b.avgScore);

  const maxLgaScore = Math.max(...lgaRanked.map(l => l.avgScore), 1);

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
            MRV Evidence Management
          </span>
          <h1 className="text-2xl font-black text-slate-900 mt-1.5">Evidence Vault</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Real document upload · Workflow states · Audit trail · Dynamic confidence · VCM/Art.6.4 readiness
          </p>
        </div>
        <div className="flex gap-2">
          {lastSweep && (
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
              <Clock size={10} />
              <span>Last sweep: {lastSweep}</span>
            </div>
          )}
          <button onClick={runSweep} disabled={sweeping}
            className="flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-900 border border-emerald-200 rounded-lg px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 font-medium disabled:opacity-60"
            data-testid="run-sweep-btn">
            {sweeping ? <><RefreshCw size={11} className="animate-spin" /> Running sweep…</> : <><Zap size={11} /> Run Governance Sweep</>}
          </button>
          <button onClick={loadVault} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Portfolio Banner */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Portfolio Evidence Overview</p>
        <div className="flex items-end gap-6 mb-4">
          <div>
            <p className="text-4xl font-black" style={{ color: portScore >= 65 ? "#34d399" : portScore >= 35 ? "#fbbf24" : "#f87171" }}>
              {portScore.toFixed(0)}/100
            </p>
            <p className="text-[10px] opacity-40 mt-1">portfolio average confidence · {ents.length} entities</p>
          </div>
          <div className="flex-1 pb-2">
            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: portScore + "%", background: portScore >= 65 ? "#34d399" : portScore >= 35 ? "#fbbf24" : "#f87171" }} />
            </div>
            <div className="flex justify-between text-[9px] opacity-30 mt-1">
              <span>0</span><span>VCM threshold (65)</span><span>100</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards — 8 enterprise metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon:FileText,     label:"Total Docs Uploaded",    value: String(kpis.total_docs    || 0), color:"#0f172a" },
          { icon:CheckCircle2, label:"Approved",               value: String(kpis.approved_docs || 0), color:"#059669" },
          { icon:Clock,        label:"Pending Review",         value: String(kpis.pending_review|| 0), color:"#d97706" },
          { icon:AlertTriangle,label:"Blocked Entities",       value: String(kpis.blocked       || 0), color:"#dc2626" },
          { icon:Target,       label:"VCM Ready",              value: String(kpis.vcm_ready     || 0), color:"#1d4ed8" },
          { icon:Shield,       label:"Art.6 Pre-Qualified",    value: String(kpis.art6_ready    || 0), color:"#7c3aed" },
          { icon:Zap,          label:"Expiring in 30d",        value: String(kpis.expiring_in_30d||0),color: (kpis.expiring_in_30d||0) > 0 ? "#d97706" : "#059669" },
          { icon:Activity,     label:"Methodology Blocked",    value: String(kpis.methodology_blocked||0), color:(kpis.methodology_blocked||0) > 0 ? "#dc2626" : "#059669" },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
              <Icon size={13} style={{ color }} />
            </div>
            <div>
              <p className="text-[10px] text-slate-400">{label}</p>
              <p className="text-xl font-black mt-0.5" style={{ color }}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* View Tabs — Entities | Reviewer Queue | Expiry */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5">
        {[
          { id:"entities",       label:"Entity Registry" },
          { id:"reviewer_queue", label:"Reviewer Queue" },
          { id:"expiry",         label:"Expiry Monitor" },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveView(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              activeView === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"
            }`}
            data-testid={`view-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Coverage by LGA */}
      <SectionCard title="Evidence Coverage by LGA — sorted by urgency (lowest first)">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {lgaRanked.map(({ lga, avgScore, count }) => {
            const tier = avgScore >= 65 ? "High" : avgScore >= 35 ? "Medium" : "Low";
            const t = TIER_STYLE[tier];
            return (
              <div key={lga} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="font-medium text-slate-700">{lga}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">{count} entities</span>
                    <span className="font-black" style={{ color: t.dot }}>{avgScore.toFixed(0)}/100</span>
                  </div>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${avgScore}%`, background: t.dot }} />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* ── REVIEWER QUEUE VIEW ─────────────────────────────────────────────── */}
      {activeView === "reviewer_queue" && (
        <ReviewerQueuePanel periodId={selectedPeriod} />
      )}

      {/* ── EXPIRY MONITOR VIEW ─────────────────────────────────────────────── */}
      {activeView === "expiry" && (
        <ExpiryMonitorPanel periodId={selectedPeriod} />
      )}

      {/* ── ENTITY REGISTRY VIEW ────────────────────────────────────────────── */}
      {activeView === "entities" && (
      <SectionCard title={`Entity Evidence Registry (${displayed.length} of ${ents.length})`}>
        <div className="p-4">
          {/* Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entity, LGA, sector…"
                className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                data-testid="vault-search" />
            </div>
            <select value={filterTier} onChange={e => setFilterTier(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid="filter-tier">
              <option value="all">All Tiers</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="Insufficient">Insufficient</option>
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white">
              <option value="score_asc">Lowest score first</option>
              <option value="score_desc">Highest score first</option>
              <option value="name">By name</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Entity","LGA","Sector","Tier","Score","Docs","VCM","Art.6","Method.","Open"].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.slice(0, 75).map(e => {
                  const conf = e.confidence || {};
                  return (
                    <tr key={e.entity_id} className="border-b border-slate-50 hover:bg-blue-50/30 cursor-pointer"
                      onClick={() => setSelectedEntity(e.entity_id)} data-testid={`vault-row-${e.entity_id}`}>
                      <td className="py-2.5 px-3 font-medium max-w-[150px] truncate">{e.name}</td>
                      <td className="py-2.5 px-3 text-slate-500">{e.lga}</td>
                      <td className="py-2.5 px-3 text-slate-500 capitalize">{(e.sector||"").replace(/_/g," ")}</td>
                      <td className="py-2.5 px-3"><TierBadge tier={conf.tier} /></td>
                      <td className="py-2.5 px-3"><ConfBar score={conf.score || 0} tier={conf.tier || "Insufficient"} /></td>
                      <td className="py-2.5 px-3 text-center">
                        <span className="font-bold">{conf.approved_docs||0}</span>
                        <span className="text-slate-400">/{conf.total_docs||0}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {conf.vcm_ready ? <CheckCircle2 size={12} className="text-emerald-500" /> : <div className="w-3 h-3 rounded-full border border-slate-200 mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {conf.art6_ready ? <CheckCircle2 size={12} className="text-blue-500" /> : <div className="w-3 h-3 rounded-full border border-slate-200 mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {conf.methodology_blocked
                          ? <XCircle size={12} className="text-red-500" title="Methodology blocked" />
                          : <CheckCircle2 size={12} className="text-emerald-400" />}
                      </td>
                      <td className="py-2.5 px-3">
                        <button className="text-[10px] bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-500 px-2 py-1 rounded font-medium"
                          onClick={ev => { ev.stopPropagation(); setSelectedEntity(e.entity_id); }}
                          data-testid={`open-entity-${e.entity_id}`}>
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </SectionCard>
      )}

      {/* Entity Detail Drawer */}
      {selectedEntity && (
        <EntityDetailDrawer
          entityId={selectedEntity}
          periodId={selectedPeriod}
          onClose={() => { setSelectedEntity(null); loadVault(); }}
        />
      )}
    </div>
  );
}

// ── Reviewer Queue Panel ──────────────────────────────────────────────────────
function ReviewerQueuePanel({ periodId }) {
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("pending");
  const [lga, setLga]             = useState("");
  const [selected, setSelected]   = useState([]);
  const [bulkAction, setBulkAction] = useState("approve");
  const [bulkNotes, setBulkNotes] = useState("");
  const [bulking, setBulking]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status_filter: filter });
      if (lga) params.append("lga", lga);
      const r = await apiFetch(`/lagos/vault/reviewer-queue/${periodId}?${params}`);
      if (!r.ok) throw new Error();
      setQueueData(await r.json());
    } catch { toast.error("Failed to load queue"); }
    finally { setLoading(false); }
  }, [periodId, filter, lga]);

  useEffect(() => { load(); }, [load]);

  const handleBulk = async () => {
    if (!selected.length) { toast.error("Select documents first"); return; }
    setBulking(true);
    try {
      const r = await apiFetch(`/lagos/vault/bulk-review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doc_ids: selected, action: bulkAction, notes: bulkNotes }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      toast.success(`Bulk ${bulkAction}: ${d.processed} documents processed`);
      setSelected([]); setBulkNotes(""); load();
    } catch { toast.error("Bulk action failed"); }
    finally { setBulking(false); }
  };

  const docs = queueData?.documents || [];
  const dash = queueData?.dashboard || {};

  return (
    <div className="space-y-4">
      {/* Queue Dashboard */}
      <div className="grid grid-cols-2 gap-3">
        {[
          ["Pending Review",  dash.pending_review  || 0, "#d97706"],
          ["Critical Pending",dash.critical_pending || 0, "#dc2626"],
        ].map(([k, v, c]) => (
          <div key={k} className="bg-white border border-slate-100 rounded-xl p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${c}15` }}>
              <Clock size={13} style={{ color: c }} />
            </div>
            <div>
              <p className="text-[10px] text-slate-400">{k}</p>
              <p className="text-xl font-black" style={{ color: c }}>{v}</p>
            </div>
          </div>
        ))}
      </div>

      <SectionCard title="Reviewer Queue">
        <div className="p-4 space-y-3">
          {/* Queue filters */}
          <div className="flex gap-2 flex-wrap">
            {["pending","approved","rejected","expiring","all"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-[10px] px-3 py-1.5 rounded-lg border font-medium ${filter === f ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                data-testid={`queue-filter-${f}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Bulk action bar */}
          {selected.length > 0 && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex-wrap">
              <span className="text-xs font-bold text-blue-700">{selected.length} selected</span>
              <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
                className="text-xs border border-blue-200 rounded px-2 py-1 bg-white" data-testid="bulk-action-select">
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="mark_expired">Mark Expired</option>
                <option value="archive">Archive</option>
              </select>
              <input value={bulkNotes} onChange={e => setBulkNotes(e.target.value)}
                placeholder="Notes…" className="text-xs border border-blue-200 rounded px-2 py-1 flex-1 min-w-[120px] bg-white" />
              <button onClick={handleBulk} disabled={bulking}
                className="text-xs bg-[#1d4ed8] text-white px-4 py-1.5 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-60"
                data-testid="bulk-execute-btn">
                {bulking ? "Processing…" : `Apply to ${selected.length}`}
              </button>
            </div>
          )}

          {/* Document list */}
          {loading ? <p className="text-sm text-slate-400">Loading queue…</p> :
            docs.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Queue empty for this filter</p> :
            <div className="space-y-1">
              {/* Select all */}
              <div className="flex items-center gap-2 text-xs text-slate-500 pb-2 border-b border-slate-100">
                <button onClick={() => setSelected(selected.length === docs.length ? [] : docs.map(d => d.doc_id))}
                  className="flex items-center gap-1 hover:text-slate-700" data-testid="select-all-btn">
                  {selected.length === docs.length ? <CheckSquare size={12} /> : <Square size={12} />}
                  {selected.length === docs.length ? "Deselect all" : "Select all"}
                </button>
                <span className="text-slate-300">|</span>
                <span>{docs.length} documents · {queueData?.filter}</span>
              </div>
              {docs.slice(0, 50).map(doc => (
                <div key={doc.doc_id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${selected.includes(doc.doc_id) ? "bg-blue-50 border-blue-200" : "bg-white border-slate-100 hover:bg-slate-50"}`}>
                  <input type="checkbox" checked={selected.includes(doc.doc_id)}
                    onChange={() => setSelected(s => s.includes(doc.doc_id) ? s.filter(x => x !== doc.doc_id) : [...s, doc.doc_id])}
                    className="flex-shrink-0" data-testid={`select-doc-${doc.doc_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-700 truncate">{doc.file_name}</span>
                      <StatusBadge status={doc.status} />
                      {doc.is_critical ? <span className="text-[8px] bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 rounded font-bold">CRITICAL</span> : null}
                    </div>
                    <p className="text-[10px] text-slate-400">{doc.entity_name} · {doc.lga} · {doc.doc_type}</p>
                    {doc.calculated_expiry && <p className="text-[9px] text-amber-600">Expires: {doc.calculated_expiry}</p>}
                  </div>
                  <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          }
        </div>
      </SectionCard>
    </div>
  );
}

// ── Expiry Monitor Panel ──────────────────────────────────────────────────────
function ExpiryMonitorPanel({ periodId }) {
  const [expiryData, setExpiryData] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!periodId) return;
    setLoading(true);
    apiFetch(`/lagos/vault/governance/expiry-status/${periodId}`)
      .then(r => r.json()).then(setExpiryData).catch(() => toast.error("Failed to load expiry data"))
      .finally(() => setLoading(false));
  }, [periodId]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading expiry data…</div>;

  return (
    <div className="space-y-4">
      {/* Expiry KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Expired Docs",      expiryData?.expired_count || 0,           "#dc2626"],
          ["Expiring in 30d",   expiryData?.expiring_count || 0,          "#d97706"],
          ["Expired Critical",  expiryData?.expired_critical_count || 0,  "#dc2626"],
          ["At-Risk Entities",  expiryData?.at_risk_entities || 0,        "#d97706"],
        ].map(([k, v, c]) => (
          <div key={k} className="bg-white border border-slate-100 rounded-xl p-4">
            <p className="text-[10px] text-slate-400">{k}</p>
            <p className="text-2xl font-black mt-1" style={{ color: v > 0 ? c : "#059669" }}>{v}</p>
          </div>
        ))}
      </div>

      {/* Expired docs list */}
      {(expiryData?.expired_docs || []).length > 0 && (
        <SectionCard title={`Expired Documents (${expiryData.expired_count}) — Require Renewal`}>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Entity","LGA","Document","Type","Expired On","Critical"].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(expiryData?.expired_docs || []).slice(0, 20).map(d => (
                  <tr key={d.doc_id} className="border-b border-slate-50 hover:bg-red-50/20">
                    <td className="py-2 px-3 font-medium truncate max-w-[120px]">{d.entity_name}</td>
                    <td className="py-2 px-3 text-slate-500">{d.lga}</td>
                    <td className="py-2 px-3 text-slate-600 truncate max-w-[120px]">{d.file_name}</td>
                    <td className="py-2 px-3 capitalize text-slate-500">{(d.doc_type||"").replace(/_/g," ")}</td>
                    <td className="py-2 px-3 text-red-600 font-bold">{d.calculated_expiry}</td>
                    <td className="py-2 px-3 text-center">{d.is_critical ? <XCircle size={12} className="text-red-500" /> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Expiring soon */}
      {(expiryData?.expiring_soon || []).length > 0 && (
        <SectionCard title={`Expiring in 30 Days (${expiryData.expiring_count}) — Action Required`}>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Entity","LGA","Document","Type","Expires On","Critical"].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(expiryData?.expiring_soon || []).slice(0, 20).map(d => (
                  <tr key={d.doc_id} className="border-b border-slate-50 hover:bg-amber-50/20">
                    <td className="py-2 px-3 font-medium truncate max-w-[120px]">{d.entity_name}</td>
                    <td className="py-2 px-3 text-slate-500">{d.lga}</td>
                    <td className="py-2 px-3 text-slate-600 truncate max-w-[120px]">{d.file_name}</td>
                    <td className="py-2 px-3 capitalize text-slate-500">{(d.doc_type||"").replace(/_/g," ")}</td>
                    <td className="py-2 px-3 text-amber-600 font-bold">{d.calculated_expiry}</td>
                    <td className="py-2 px-3 text-center">{d.is_critical ? <AlertTriangle size={12} className="text-amber-500" /> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {(expiryData?.expired_count || 0) === 0 && (expiryData?.expiring_count || 0) === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <CheckCircle2 size={24} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-bold text-emerald-700">No expired or expiring documents</p>
          <p className="text-xs text-emerald-600 mt-1">Portfolio is clear of expiry risk</p>
        </div>
      )}
    </div>
  );
}
